import { Colors, Lightning, Registry } from '@lightningjs/sdk';
import Button, { ButtonTemplateSpec } from 'components/common/buttons/Button';
import { getFontFaceFromStyle } from 'support/textUtils';
import { getImageTextureObj } from 'support/generalUtils';

export interface ToggleListButtonTemplateSpec extends ButtonTemplateSpec {
  isSelected: boolean;
  backgroundColor: { focused?: number; unfocused?: number; clicked?: number };
  fontColor: { focused?: number; unfocused?: number; clicked?: number };

  Underline: Lightning.textures.RectangleTexture;
}

const ICON_SIZE = 16;
const SELECTED_ICONS = {
  focused: getImageTextureObj(
    'static/images/checkmark-dark.svg',
    ICON_SIZE,
    ICON_SIZE,
  ),
  unfocused: getImageTextureObj(
    'static/images/checkmark-light.svg',
    ICON_SIZE,
    ICON_SIZE,
  ),
  clicked: getImageTextureObj(
    'static/images/checkmark-gray.svg',
    ICON_SIZE,
    ICON_SIZE,
  ),
} as const;

const DEFAULT_MARGIN = 30;
const DEFAULT_HEIGHT = 22;
const DEFAULT_WIDTH = 786;
const PADDING_LEFT = 0;
const PADDING_RIGHT = 30;
const PADDING_Y = 15;
const LINE_HEIGHT = 22;

const UNDERLINE_HEIGHT = 2;

const CLICKED_DELAY = 300;

export default class ToggleListButton
  extends Button<ToggleListButtonTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<ToggleListButtonTemplateSpec>
{
  private _isSelected = false;
  private _isClicking = false;

  override _backgroundColor = {
    focused: Colors('highlight').get(),
    unfocused: Colors('buttonInactive').get(),
    disabled: Colors('disabledButton').get(),
    clicked: Colors('highlight').get(),
  };

  override _fontColor = {
    focused: Colors('activeText').get(),
    unfocused: Colors('text').get(),
    disabled: Colors('disabledButtonText').get(),
    clicked: Colors('activeText').get(),
  };

  override _buttonClass = 'ToggleListButton';

  private _Underline = this.getByRef('Underline')!;

  override get title() {
    return this.label;
  }

  override set backgroundColor(value: {
    focused?: number;
    unfocused?: number;
    clicked?: number;
  }) {
    super.backgroundColor = value;
    this._backgroundColor.clicked =
      value.clicked ?? this._backgroundColor.clicked;
  }

  override set fontColor(value: {
    focused?: number;
    unfocused?: number;
    clicked?: number;
  }) {
    super.fontColor = value;
    this._fontColor.clicked = value.clicked ?? this._fontColor.clicked;
  }

  get isSelected() {
    return this._isSelected;
  }

  set isSelected(isSelected: ToggleListButtonTemplateSpec['isSelected']) {
    this._isSelected = isSelected;

    if (!this._isClicking) {
      this.resetStyle();
    } else if (!isSelected) {
      // if no longer selected while clicking update icon to unselected
      this.updateIconVisibility();
    }
  }

  static override _template(): Lightning.Component.Template<ToggleListButtonTemplateSpec> {
    return {
      ...super._template(),
      h: DEFAULT_HEIGHT,
      shader: {},
      flex: {
        direction: 'row',
        alignItems: 'center',
        paddingTop: PADDING_Y,
        paddingBottom: PADDING_Y,
        paddingLeft: PADDING_LEFT,
        paddingRight: PADDING_RIGHT,
      },
      Underline: {
        y: h => h,
        w: DEFAULT_WIDTH,
        h: UNDERLINE_HEIGHT,
        flexItem: false,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: 0,
          stroke: UNDERLINE_HEIGHT,
          strokeColor: Colors('labelText').get(),
        },
        rect: true,
        color: Colors('labelText').get(),
        alpha: 1,
        clipping: false,
      },
    };
  }

  override _setup() {
    super._setup();
    this.paddingH = 0;
    this.margin = DEFAULT_MARGIN;
    this.minWidth = this.minWidth || DEFAULT_WIDTH;

    this.startIcon = {
      focused: SELECTED_ICONS.focused,
      unfocused: SELECTED_ICONS.unfocused,
    };

    this._Label.patch({
      flexItem: { marginLeft: this.margin },
      text: {
        fontFace: getFontFaceFromStyle('regular'),
        lineHeight: LINE_HEIGHT,
      },
    });

    this._StartIcon.patch({
      w: ICON_SIZE,
      h: ICON_SIZE,
      flexItem: { marginLeft: this.margin },
    });
    this.updateIconVisibility();

    this._Underline.patch({
      alpha: 1,
    });
  }

  override _focus() {
    super._focus();

    this.setStartIcon(SELECTED_ICONS.focused);
    this.updateIconVisibility();

    this._Underline.patch({ alpha: 0 });
  }

  override _unfocus() {
    super._unfocus();

    this.setStartIcon(SELECTED_ICONS.unfocused);
    this.updateIconVisibility();

    this._Underline.patch({ alpha: 1 });
  }

  override _handleEnter() {
    this.handleClickStyle();

    super._handleEnter();
  }

  private handleStyle(type: keyof typeof SELECTED_ICONS) {
    this.patch({
      color: this._backgroundColor[type],
    });

    this._Label.patch({
      text: { textColor: this._fontColor[type] },
    });

    this.setSmooth('color', this._backgroundColor[type]);

    this.setStartIcon(SELECTED_ICONS[type]);
    this.updateIconVisibility();
  }

  private updateIconVisibility() {
    this._StartIcon.patch({
      alpha: this._isSelected || this._isClicking ? 1 : 0,
    });
  }

  private handleClickStyle() {
    // we shouldn't show the click style if its already clicking or we don't have focus
    if (this._isClicking || !this.hasFocus()) return;

    this._isClicking = true;

    this.handleStyle('clicked');

    Registry.setTimeout(() => {
      this._isClicking = false;
      this.resetStyle();
    }, CLICKED_DELAY);
  }

  private resetStyle() {
    this.handleStyle(this.hasFocus() ? 'focused' : 'unfocused');
  }
}
